import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Event, Objective, parseLocalDate, formatDateForDisplay } from '../lib/supabase';
import './Overview.css';

interface OverviewProps {
  events: Event[];
  objectives: Objective[];
}

const Overview: React.FC<OverviewProps> = ({ events, objectives }) => {
  const navigate = useNavigate();
  
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return events
      .filter(e => {
        const eventDate = parseLocalDate(e.start_date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today;
      })
      .slice(0, 5);
  }, [events]);

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;
    const className = priority.toLowerCase() === 'high' ? 'badge-high' : 
                     priority.toLowerCase() === 'medium' ? 'badge-medium' : 'badge-low';
    return <span className={`badge ${className}`}>{priority}</span>;
  };

  return (
    <div className="overview">
      <div className="grid">
        <div className="col-12">
          <div className="card">
            <h3>2026 Objectives</h3>
            {objectives.length === 0 ? (
              <p className="muted">No objectives yet. Add some in the Objectives section.</p>
            ) : (
              <ul className="objectives-list">
                {objectives.map(obj => (
                  <li key={obj.id}>
                    <span>{obj.content}</span>
                    {obj.category && <span className="muted"> • {obj.category}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="events-action">
            <button 
              className="btn-show-calendar"
              onClick={() => navigate('/calendar')}
            >
              Show me Calendar
            </button>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <h3>Upcoming Events</h3>
            {upcomingEvents.length === 0 ? (
              <p className="muted">No upcoming events. Add events in the Calendar section.</p>
            ) : (
              <div className="events-list">
                {upcomingEvents.map(event => (
                  <div key={event.id} className="event-item">
                    <div className="event-header">
                      <h4>{event.title}</h4>
                      {getPriorityBadge(event.priority)}
                    </div>
                    <div className="event-meta">
                      <span className="event-date">{formatDateForDisplay(event.start_date)}</span>
                      {event.end_date && event.end_date !== event.start_date && (
                        <span className="muted"> - {formatDateForDisplay(event.end_date)}</span>
                      )}
                      {event.location && (
                        <span className="muted"> • {event.location}</span>
                      )}
                    </div>
                    {event.description && (
                      <p className="event-description">{event.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;

